import { EntityIcon } from '@common/Entity/EntityIcons';
import { Entity } from '@common/types/Entity';
import { getIDEUrl, getSqlIDEUrl, Private_Routes } from '@common/url';
import { renderColumnWithEllipsis } from '@common/utils/renderUtils';
import { useUDFsData } from '@pages/Transpiler/useUDFsData';
import { EditBIcon } from '@prophecy/icons';
import { ExtractUrlPathVariables } from '@prophecy/interfaces/generic';
import { theme } from '@prophecy/theme';
import { ColumnsType, Stack, Table, TableActions, TableIconButton } from '@prophecy/ui-v3';
import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { UDfInfoType } from '../../../../data/apis/transpileTypes';
import { Error } from '../../Import/utils';
import { getStatusColumn, hasStatusWithError, InfoTableHeader, StyledNameContainer } from '../util';

export const UdfsTable = ({
  isBulk,
  id,
  isSql,
  projectId,
  projectName
}: {
  projectName?: string;
  isBulk: boolean;
  id: string;
  isSql?: boolean;
  projectId?: string;
}) => {
  const params = useParams<ExtractUrlPathVariables<typeof Private_Routes.TranspilerDetail.url>>();

  const udfColumns: ColumnsType<UDfInfoType> = [
    {
      key: 'name',
      dataIndex: 'name',
      title: 'Name',
      filter: {
        type: 'search',
        searchInputProps: {
          placeholder: 'Search..',
          'data-test-id': 'udfs-table-name-search-input'
        }
      },
      render: (name) => {
        return (
          <Stack direction='horizontal' gap={theme.spaces.x4} width='100%' alignY='center'>
            <EntityIcon entity={Entity.Function} size='xs' />
            <StyledNameContainer grow='1'>{renderColumnWithEllipsis(name)}</StyledNameContainer>
          </Stack>
        );
      }
    },
    isSql
      ? {}
      : {
          key: 'pipelineName',
          dataIndex: 'pipelineName',
          title: 'Pipeline',
          sortable: true,
          render: (name) => {
            return (
              <Stack direction='horizontal' gap={theme.spaces.x4} width='100%' alignY='center'>
                <EntityIcon entity={Entity.Pipeline} size='xs' />
                <StyledNameContainer grow='1'>{renderColumnWithEllipsis(name)}</StyledNameContainer>
              </Stack>
            );
          }
        },
    getStatusColumn(),
    {
      align: 'center',
      dataIndex: 'actions',
      key: 'actions',
      width: '60px',
      resizable: false,
      render: (_: string, rowData) => {
        const uid = rowData.id;
        const name = rowData.name;
        if (hasStatusWithError(rowData.status)) {
          return null;
        }

        let url = getIDEUrl(
          {
            uid: uid,
            entity: Entity.Pipeline
          },
          {
            functionName: rowData.name,
            functionProject: projectName as string,
            transpilerSource: params.type,
            transpilerVersion: params.version,
            transpilerId: id
          }
        );
        if (isSql) {
          url = getSqlIDEUrl({ uid: projectId as string, entity: Entity.Macro.toLowerCase(), name });
        }
        return (
          <TableActions gap={theme.spaces.x12} alignY='center'>
            {uid && (
              <TableIconButton
                icon={<EditBIcon type='default' />}
                elementType='link'
                to={url}
                aria-label={`Edit ${name}`}
                data-test-id='udfs-table-edit-button'
              />
            )}
          </TableActions>
        );
      }
    }
  ];

  const { data, isError, isLoading } = useUDFsData(id, !isBulk);

  const udfs = useMemo(() => data?.data?.udfs || [], [data?.data]);

  if (isError) {
    return <Error message='Some error occurred' />;
  }

  return (
    <Stack width='100%' height='100%' gap={theme.spaces.x12}>
      <InfoTableHeader title='Functions' count={udfs.length} />
      <Stack grow='1'>
        <Table
          fixedHeader={true}
          height='100%'
          loading={isLoading}
          width='100%'
          virtualize={true}
          rowKey={'rowKey'}
          columns={udfColumns}
          data-test-id='imported-info-udfs-table'
          dataSource={udfs.map((udf, i) => ({ ...udf, rowKey: i }))}
        />
      </Stack>
    </Stack>
  );
};
